import { t } from 'i18next';

// ============================================================================
// CANONICAL MARKETING CONDITION DEFINITIONS
// ============================================================================
// This file is the SINGLE SOURCE OF TRUTH for all marketing condition metadata.
// All other files should import from here, not redefine condition data.
// ============================================================================

export interface SubConditionDefinition {
    belongsToConditionId: number;
    conditionId: number;
    description?: string;
    conditionDefinition?: Partial<MarketingConditionSchema>;
    field?: string;
    operator?: string;
    valueMapping?: {
        from: string;
        to: string;
        transform?: (value: any) => any;
    };
    compositeValues?: Record<string, any>;
    fieldValueExample?: any;
}

export const commonTemplateKeywords = [
    'customer_name',
    'customer_email',
    'customer_phone_number',
    'customer_birthday',
    'customer_address',
    'customer_city',
    'customer_zip_code',
    'booking_link',
    'outlet_name',
    'outlet_address',
    'outlet_city',
    'outlet_zip_code',
    'outlet_phone_number',
    'outlet_email',
    'outlet_website',
    'outlet_facebook',
    'outlet_tiktok',
    'outlet_instagram',
];

export interface MarketingConditionSchema {
    conditionId: number;
    condition_render_id: number;
    operator: string;
    description: string;
    triggerId: number;
    group_id: number;
    group_name: string;
    group_label: string;
    condition_label: string;
    group_sequence: number;
    settingsDescription?: string;
    render_sequence?: number;
    subconditions?: SubConditionDefinition[];
    fieldValueExample?: any;
    field?: string;
}

export const marketingConditions: MarketingConditionSchema[] = [
    // ============================================================================
    // CUSTOMER PROFILE CONDITIONS
    // ============================================================================
    {
        conditionId: 18,
        condition_render_id: 1,
        field: 'birthday',
        operator: 'BIRTHDAY_BETWEEN',
        description: t('Marketing.CON1Desc'),
        triggerId: 10,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON1Label'),
        settingsDescription: t('Marketing.CON1SetDesc'),
        fieldValueExample: [1, 30],
        subconditions: [
            {
                belongsToConditionId: 18,
                conditionId: 18,
                description: 'Birthday exact day (if exact day mode selected)',
                conditionDefinition: {
                    conditionId: 18,
                    condition_render_id: 0,
                    operator: 'BIRTHDAY_ON_DATES',
                    description: 'customer birth date on exact dates',
                    triggerId: 10,
                    group_id: 1,
                    group_name: 'CustomerProfile',
                    group_label: 'Customer Profile',
                    group_sequence: 1,
                    condition_label: 'Birthday on dates',
                    fieldValueExample: null,
                },
            },
            {
                belongsToConditionId: 18,
                conditionId: 18,
                description: 'Birthday milestone age (if milestone mode selected)',
                conditionDefinition: {
                    conditionId: 18,
                    condition_render_id: 0,
                    field: 'birthday',
                    operator: 'BIRTHDAY_BETWEEN',
                    description: 'customer birthday milestone age',
                    triggerId: 10,
                    group_id: 1,
                    group_name: 'CustomerProfile',
                    group_label: 'Customer Profile',
                    group_sequence: 1,
                    condition_label: 'Birthday milestone age',
                    fieldValueExample: 30,
                },
            },
        ],
    },
    {
        conditionId: 23,
        condition_render_id: 2,
        field: 'created_at',
        operator: 'WITHIN_LAST_HOURS',
        description: t('Marketing.CON2Desc'),
        triggerId: 11,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON2Label'),
        settingsDescription: t('Marketing.CON2SetDesc'),
        subconditions: [],
    },
    {
        conditionId: 7,
        condition_render_id: 4,
        field: 'birthday',
        operator: 'AGE_BETWEEN',
        description: t('Marketing.CON4Desc'),
        triggerId: 6,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON4Label'),
        settingsDescription: t('Marketing.CON4SetDesc'),
        subconditions: [],
    },
    {
        conditionId: 1,
        condition_render_id: 6,
        field: 'zip_code',
        operator: 'BETWEEN',
        description: t('Marketing.CON6Desc'),
        triggerId: 1,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON6Label'),
        settingsDescription: t('Marketing.CON6SetDesc'),
        subconditions: [],
    },

    // ============================================================================
    // BOOKING BEHAVIOR CONDITIONS
    // ============================================================================
    {
        conditionId: 2,
        condition_render_id: 7,
        field: 'booking_datetime_start',
        operator: 'BEFORE_HOURS',
        description: t('Marketing.CON7Desc'),
        triggerId: 1,
        group_id: 2,
        group_name: 'BookingBehavior',
        group_label: 'Booking Behavior',
        group_sequence: 2,
        condition_label: t('Marketing.CON7Label'),
        settingsDescription: t('Marketing.CON7SetDesc'),
        subconditions: [],
        fieldValueExample: ['30'],
    },
    {
        conditionId: 6,
        condition_render_id: 8,
        field: 'service_id',
        operator: 'IN',
        description: t('Marketing.CON8Desc'),
        triggerId: 5,
        group_id: 2,
        group_name: 'BookingBehavior',
        group_label: 'Booking Behavior',
        group_sequence: 2,
        condition_label: t('Marketing.CON8Label'),
        settingsDescription: t('Marketing.CON8SetDesc'),
        subconditions: [
            {
                belongsToConditionId: 6,
                conditionId: 2,
                description: 'Days range condition with [daysRange, e.g., 60]',
                conditionDefinition: {
                    conditionId: 2,
                    condition_render_id: 8,
                    field: 'booking_datetime_start',
                    operator: 'WITHIN_LAST_HOURS',
                    description: 'Customer Has Any Booking in last X days',
                    triggerId: 1,
                    group_id: 2,
                    group_name: 'BookingBehavior',
                    group_label: 'Booking Behavior',
                    group_sequence: 2,
                    condition_label: 'Active Booking',
                },
            },
            {
                belongsToConditionId: 6,
                conditionId: 6,
                description: 'Service IDs condition with [serviceIds]',
                conditionDefinition: {
                    conditionId: 6,
                    condition_render_id: 8,
                    field: 'service_id',
                    operator: 'IN',
                    description: 'Customer has a booking within period for selected service',
                    triggerId: 5,
                    group_id: 2,
                    group_name: 'BookingBehavior',
                    group_label: 'Booking Behavior',
                    group_sequence: 2,
                    condition_label: 'Active booking – selected service',
                },
            },
            {
                belongsToConditionId: 6,
                conditionId: 25,
                description: 'Status condition with [status]',
                conditionDefinition: {
                    conditionId: 7,
                    condition_render_id: 9,
                    field: 'status',
                    operator: 'IN',
                    description: 'Customer has a booking within period for selected service',
                    triggerId: 6,
                    group_id: 2,
                    group_name: 'BookingBehavior',
                    group_label: 'Booking Behavior',
                    group_sequence: 2,
                    condition_label: 'Active booking – selected service',
                },
            },
        ],
    },
    {
        conditionId: 6,
        condition_render_id: 9,
        field: 'status',
        operator: 'IN',
        description: t('Marketing.CON9Desc'),
        triggerId: 10,
        group_id: 2,
        group_name: 'BookingBehavior',
        group_label: 'Booking Behavior',
        group_sequence: 2,
        condition_label: t('Marketing.CON9Label'),
        settingsDescription: t('Marketing.CON9SetDesc'),
        subconditions: [
            {
                belongsToConditionId: 6,
                conditionId: 25,
                description: 'Service IDs condition with [serviceIds]',
                conditionDefinition: {
                    conditionId: 25,
                    condition_render_id: 9,
                    field: 'service_id',
                    operator: 'IN',
                    description: 'Customer has a booking within period for selected service',
                    triggerId: 5,
                    group_id: 2,
                    group_name: 'BookingBehavior',
                    group_label: 'Booking Behavior',
                    group_sequence: 2,
                    condition_label: 'Active booking – selected service',
                },
            },
            {
                belongsToConditionId: 6,
                conditionId: 2,
                description: 'Days range condition with [daysRange, e.g., 60]',
                conditionDefinition: {
                    conditionId: 2,
                    condition_render_id: 9,
                    field: 'booking_datetime_start',
                    operator: 'WITHIN_LAST_HOURS',
                    description: 'Customer Has Any Booking in last X days',
                    triggerId: 1,
                    group_id: 2,
                    group_name: 'BookingBehavior',
                    group_label: 'Booking Behavior',
                    group_sequence: 2,
                    condition_label: 'Active Booking',
                },
            },
        ],
    },

    // ============================================================================
    // PURCHASES BEHAVIOR CONDITIONS
    // ============================================================================
    {
        conditionId: 20,
        condition_render_id: 10,
        field: 'product_id',
        operator: 'IN',
        description: t('Marketing.CON10Desc'),
        triggerId: 12,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 1,
        condition_label: t('Marketing.CON10Label'),
        settingsDescription: t('Marketing.CON10SetDesc'),
        subconditions: [],
        fieldValueExample: ['09575f05-1381-4080-b008-cdf6cb8737f6'],
    },
    {
        conditionId: 21,
        field: 'sales_date',
        operator: 'BEFORE_HOURS',
        description: t('Marketing.CON11Desc'),
        triggerId: 2,
        condition_render_id: 11,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 2,
        condition_label: t('Marketing.CON11Label'),
        settingsDescription: t('Marketing.CON11SetDesc'),
        subconditions: [
            {
                belongsToConditionId: 21,
                conditionId: 10,
                description: 'Product IDs condition with [productIds]',
                conditionDefinition: {
                    conditionId: 10,
                    condition_render_id: 0,
                    field: 'product_id',
                    operator: 'IN',
                    description: 'customer has purchased a specific item name',
                    triggerId: 12,
                    group_id: 3,
                    group_name: 'PurchasesBehavior',
                    group_label: 'Purchases Behavior',
                    group_sequence: 3,
                    condition_label: 'Purchase by item name',
                },
            },
        ],
    },
    {
        conditionId: 24,
        condition_render_id: 12,
        field: 'product_category_id',
        operator: 'IN',
        description: t('Marketing.CON12Desc'),
        triggerId: 13,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 3,
        condition_label: t('Marketing.CON12Label'),
        settingsDescription: t('Marketing.CON12SetDesc'),
        subconditions: [],
    },
    {
        conditionId: 21,
        condition_render_id: 13,
        field: 'sales_date',
        operator: 'BEFORE_HOURS',
        description: t('Marketing.CON13Desc'),
        triggerId: 14,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 4,
        condition_label: t('Marketing.CON13Label'),
        settingsDescription: t('Marketing.CON13SetDesc'),
        subconditions: [
            {
                belongsToConditionId: 21,
                conditionId: 24,
                description: 'Category IDs condition with [categoryIds]',
                conditionDefinition: {
                    conditionId: 24,
                    condition_render_id: 0,
                    field: 'product_category_id',
                    operator: 'IN',
                    description: 'customer has purchased from a product group',
                    triggerId: 13,
                    group_id: 3,
                    group_name: 'PurchasesBehavior',
                    group_label: 'Purchases Behavior',
                    group_sequence: 3,
                    condition_label: 'Purchase by product group',
                },
            },
        ],
    },
    {
        conditionId: 5,
        condition_render_id: 14,
        field: 'net_total',
        operator: 'AGGREGATE_GT',
        description: t('Marketing.CON14Desc'),
        triggerId: 4,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 5,
        condition_label: t('Marketing.CON14Label'),
        settingsDescription: t('Marketing.CON14SetDesc'),
        subconditions: [],
    },
    {
        conditionId: 5,
        condition_render_id: 15,
        field: 'net_total',
        operator: 'AGGREGATE_GT',
        description: t('Marketing.CON15Desc'),
        triggerId: 15,
        group_id: 3,
        group_name: 'PurchasesBehavior',
        group_label: 'Purchases Behavior',
        group_sequence: 3,
        render_sequence: 6,
        condition_label: t('Marketing.CON15Label'),
        settingsDescription: t('Marketing.CON15SetDesc'),
        subconditions: [
            {
                belongsToConditionId: 5,
                conditionId: 21,
                description: 'Days period condition with [daysValue]',
                conditionDefinition: {
                    conditionId: 21,
                    condition_render_id: 0,
                    field: 'sales_date',
                    operator: 'BEFORE_HOURS',
                    description: 'Revenue Within period',
                    triggerId: 14,
                    group_id: 3,
                    group_name: 'PurchasesBehavior',
                    group_label: 'Purchases Behavior',
                    group_sequence: 3,
                    condition_label: 'Purchase by product group – period',
                },
            },
        ],
    },

    // ============================================================================
    // COMMUNICATION
    // ============================================================================

    {
        conditionId: 26,
        condition_render_id: 19,
        operator: 'IN',
        description: t('Marketing.CON19Desc'),
        triggerId: 15,
        group_id: 6,
        group_name: 'Communication',
        group_label: 'Communication',
        group_sequence: 3,
        render_sequence: 1,
        condition_label: t('Marketing.CON19Label'),
        settingsDescription: t('Marketing.CON19SetDesc'),
        fieldValueExample: ['CLICKED', 'OPENED', 'NOT_OPENED'],
        subconditions: [
            {
                belongsToConditionId: 26,
                conditionId: 27,
                description: 'Before days condition with [daysValue]',
                conditionDefinition: {
                    conditionId: 27,
                    condition_render_id: 0,
                    field: 'email_clicked_at',
                    operator: 'BEFORE_HOURS',
                    description: 'customer has previously received/opened/not opened a campaign',
                    triggerId: 15,
                    group_id: 6,
                    group_name: 'Communication',
                    group_label: 'Communication',
                    group_sequence: 6,
                    condition_label: 'Previous campaign – days',
                },
            },
            {
                belongsToConditionId: 26,
                conditionId: 29,
                description: 'Selected campaign',
                conditionDefinition: {
                    conditionId: 29,
                    condition_render_id: 0,
                    field: 'campaign_id',
                    operator: 'IN',
                    description: 'customer has selected campaign',
                    triggerId: 15,
                    group_id: 6,
                    group_name: 'Communication',
                    group_label: 'Communication',
                    group_sequence: 6,
                    condition_label: 'Selected campaign status',
                    fieldValueExample: ['612f7d51-1ae1-442c-931d-b143e31b7844'],
                },
            },
        ],
    },

    // ============================================================================
    // GIFT CARD AND CLIP CARD CONDITIONS
    // ============================================================================
    {
        conditionId: 16,
        condition_render_id: 17,
        field: 'residue_value',
        operator: '>',
        description: t('Marketing.CON17Desc'),
        triggerId: 10,
        group_id: 5,
        group_name: 'GiftCardAndClipCard',
        group_label: 'Gift Card and Clip Card',
        group_sequence: 5,
        condition_label: t('Marketing.CON17Label'),
        settingsDescription: t('Marketing.CON17SetDesc'),
        subconditions: [],
    },
    {
        conditionId: 9,
        condition_render_id: 20,
        field: 'expiry_date',
        operator: 'WITHIN_NEXT_HOURS',
        description: t('Marketing.CON20Desc'),
        triggerId: 9,
        group_id: 5,
        group_name: 'GiftCardAndClipCard',
        group_label: 'Gift Card and Clip Card',
        group_sequence: 5,
        condition_label: t('Marketing.CON20Label'),
        settingsDescription: t('Marketing.CON20SetDesc'),
        fieldValueExample: ['30'],
        subconditions: [],
    },
    {
        conditionId: 13,
        condition_render_id: 18,
        field: 'expiry_date',
        operator: 'WITHIN_NEXT_HOURS',
        description: t('Marketing.CON18Desc'),
        triggerId: 10,
        group_id: 4,
        group_name: 'GiftCardAndClipCard',
        group_label: 'Gift Card and Clip Card',
        group_sequence: 4,
        condition_label: t('Marketing.CON18Label'),
        settingsDescription: t('Marketing.CON18SetDesc'),
        fieldValueExample: ['2'],
        subconditions: [
            {
                belongsToConditionId: 13,
                conditionId: 14,
                description: 'Max clips condition with maxValue and < operator',
                conditionDefinition: {
                    conditionId: 14,
                    condition_render_id: 0,
                    field: 'residue_punches',
                    operator: '<',
                    description: 'clip card has a certain number of clips remaining.',
                    triggerId: 10,
                    group_id: 4,
                    group_name: 'GiftCardAndClipCard',
                    group_label: 'Gift Card and Clip Card',
                    group_sequence: 4,
                    condition_label: 'Clip card punches',
                },
            },
        ],
    },

    // ============================================================================
    // UNRENDERED CONDITIONS (WHICH ARE NOT RENDERED IN THE UI BUT HAVE USE FOR TEMPLATES KEYWORDS)
    // ============================================================================
    {
        conditionId: 8,
        condition_render_id: 0,
        field: 'marketing_permission',
        operator: '=',
        description: t('Marketing.CON0_8Desc'),
        triggerId: 7,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON0_8Label'),
        settingsDescription: t('Marketing.CON0_8SetDesc'),
    },
    {
        conditionId: 17,
        condition_render_id: 0,
        field: 'customer_group',
        operator: 'IN',
        description: t('Marketing.CON0_17Desc'),
        triggerId: 7,
        group_id: 1,
        group_name: 'CustomerProfile',
        group_label: 'Customer Profile',
        group_sequence: 1,
        condition_label: t('Marketing.CON0_17Label'),
        settingsDescription: t('Marketing.CON0_17SetDesc'),
    },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find a condition by conditionId
 */
export function getConditionById(conditionId: number): MarketingConditionSchema | undefined {
    return marketingConditions.find((c) => c.conditionId === conditionId);
}

/**
 * Find a condition by condition_render_id
 */
export function getConditionByRenderId(renderId: number): MarketingConditionSchema | null {
    const condition = marketingConditions.find((c) => c.condition_render_id === renderId);
    return condition || null;
}
/**
 * Grouped condition structure used for filtering and display
 */
export interface GroupedCondition {
    groupName: string;
    groupLabel: string;
    groupSequence: number;
    conditions: MarketingConditionSchema[];
}

/**
 * Group marketing conditions by group_name and sort them
 * Filters out conditions with condition_render_id: 0 (no renderer)
 * Sorts conditions within each group by render_sequence, condition_render_id, then conditionId
 * Sorts groups by group_sequence
 */
export function groupMarketingConditions(): GroupedCondition[] {
    const groups = new Map<string, MarketingConditionSchema[]>();

    marketingConditions.forEach((condition) => {
        // Skip conditions with condition_render_id: 0 (no renderer)
        if (condition.condition_render_id === 0) {
            return;
        }
        const groupName = condition.group_name;
        if (!groups.has(groupName)) {
            groups.set(groupName, []);
        }
        groups.get(groupName)!.push(condition);
    });

    // Convert to array and sort by group_sequence
    return Array.from(groups.entries())
        .map(([groupName, conditions]) => ({
            groupName,
            groupLabel: conditions[0]?.group_label || groupName,
            groupSequence: conditions[0]?.group_sequence || 0,
            conditions: conditions.sort((a, b) => {
                // First sort by render_sequence if available
                const aSeq = a.render_sequence ?? 999;
                const bSeq = b.render_sequence ?? 999;
                if (aSeq !== bSeq) {
                    return aSeq - bSeq;
                }
                // Fall back to condition_render_id if render_sequence is not set
                if (a.condition_render_id !== b.condition_render_id) {
                    return a.condition_render_id - b.condition_render_id;
                }
                // Finally sort by conditionId
                return a.conditionId - b.conditionId;
            }),
        }))
        .sort((a, b) => a.groupSequence - b.groupSequence);
}

/**
 * Filter grouped conditions based on search query
 * Searches in condition_label, description, and field
 */
export function filterGroupedConditions(
    groupedConditions: GroupedCondition[],
    searchQuery: string,
): GroupedCondition[] {
    if (!searchQuery.trim()) return groupedConditions;

    return groupedConditions
        .map((group) => ({
            ...group,
            conditions: group.conditions.filter(
                (condition) =>
                    condition.condition_label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    condition.description.toLowerCase().includes(searchQuery.toLowerCase()),
            ),
        }))
        .filter((group) => group.conditions.length > 0);
}

// CONFIGURATION FOR CONFLICTS IN CASE OF COMBINING CONDITIONS (WAITING FOR APPROVAL TO ADD)
// {
//   "groups": {
//     "birthday": [1],
//     "age": [4],
//     "created_at": [2],
//     "zip": [6],
//     "booking": [7, 8, 9],
//     "purchase": [10, 11, 12, 13, 14, 15],
//     "card": [17, 18],
//     "campaign": [19]
//   },
//   "conflicts": [
//     {
//       "groups": ["birthday", "age"],
//       "message": "Birthday‑based and age‑based conditions are based on the same customer information (date of birth). Using both will result in no customers because a person cannot be both a specific age and have a birthday in a specific range at the same time."
//     },
//     {
//       "groups": ["booking", "card"],
//       "message": "Booking activity and gift/clip card usage are separate aspects of customer behavior. Combining them will create an extremely narrow audience that is unlikely to yield meaningful results."
//     },
//     {
//       "groups": ["purchase", "card"],
//       "message": "Purchase history and gift/clip card usage are independent; combining them will over‑restrict your audience and is not recommended."
//     },
//     {
//       "groups": ["campaign", "all"],
//       "message": "Previous campaign engagement is unrelated to other customer criteria. Combining it with any other condition will produce an overly specific segment that may have very few or zero customers."
//     }
//   ]
// }
